/**
 * God of Creation - Game Action Definitions
 * All state changes occur only through actions
 */

import type { ActionHandler } from '@app-games/core';
import { createInitialState, MAX_ENERGY, ENERGY_RECHARGE_INTERVAL_MS, type CategoryFilter, type GameState } from './state.js';
import {
  CATEGORY_ICONS,
  KNOWN_RECIPES_BY_KEY,
  MILESTONES,
  createGeneratedElement,
  getMilestoneElement,
  resolveElementIcon,
  toComboKey,
  type ElementCategory,
  type ElementInfo,
  type MilestoneDefinition
} from './content.js';

const OPENING_PAGE_LAST_INDEX = 3;
const COMBINE_DELAY_SECONDS = 0.75;

const NICKNAME_PATTERN = /^[a-zA-Z0-9]{2,12}$/;
const TUTORIAL_COMBO_KEY = toComboKey('Water', 'Fire');
const MILESTONE_NAMES = new Set(MILESTONES.map((m) => m.name));

function countDiscoveries(inventory: string[]): number {
  return inventory.filter((name) => !MILESTONE_NAMES.has(name)).length;
}

function getUnclaimedMilestone(inventory: string[]): MilestoneDefinition | null {
  const count = countDiscoveries(inventory);
  for (const m of MILESTONES) {
    if (count >= m.threshold && !inventory.includes(m.name)) return m;
  }
  return null;
}

function grantAllEarnedMilestones(
  inventory: string[],
  elements: Record<string, ElementInfo>
): { inventory: string[]; elements: Record<string, ElementInfo> } {
  let nextInventory = inventory;
  let nextElements = elements;
  for (const m of MILESTONES) {
    if (countDiscoveries(nextInventory) >= m.threshold && !nextInventory.includes(m.name)) {
      const el = getMilestoneElement(m);
      nextInventory = [...nextInventory, el.name];
      nextElements = { ...nextElements, [el.name]: el };
    }
  }
  return { inventory: nextInventory, elements: nextElements };
}

export type GameAction =
  | { type: 'SET_RUNNING'; payload: { running: boolean } }
  | { type: 'START_FROM_TITLE' }
  | { type: 'ADVANCE_OPENING' }
  | { type: 'SET_NICKNAME_DRAFT'; payload: { value: string } }
  | { type: 'CONFIRM_NICKNAME'; payload: { nickname: string } }
  | { type: 'SET_AUTH_TOKEN'; payload: { token: string | null } }
  | {
    type: 'RESTORE_SESSION';
    payload: {
      nickname: string;
      token: string | null;
      inventory: string[];
      hasSeenOpening?: boolean;
      tutorialCompleted?: boolean;
      firstDiscoveries?: string[];
      serverElements?: Record<string, { tier: number; category: string; depth: number; image_url: string | null; discovered_count: number; description: string | null }>;
    };
  }
  | { type: 'SET_ACTIVE_TAB'; payload: { tab: GameState['activeTab'] } }
  | { type: 'SET_SEARCH_QUERY'; payload: { query: string } }
  | { type: 'SET_SORT_MODE'; payload: { mode: GameState['sortMode'] } }
  | { type: 'SET_CATEGORY_FILTER'; payload: { filter: CategoryFilter } }
  | { type: 'SELECT_ELEMENT'; payload: { name: string } }
  | { type: 'REMOVE_SLOT'; payload: { slot: 'A' | 'B' } }
  | { type: 'CLEAR_PENDING_COMBINE' }
  | { type: 'RESOLVE_PENDING_COMBINE'; payload: { a: string; b: string } }
  | {
    type: 'APPLY_SERVER_COMBINE_RESULT';
    payload: {
      a: string;
      b: string;
      result: string;
      tier: number;
      category: ElementCategory;
      description?: string | null;
      depth?: number;
      isNew: boolean;
      isFirstEver: boolean;
      firstDiscoverer: string;
      imageUrl?: string | null;
      discoveredCount?: number;
    };
  }
  | { type: 'CLOSE_RESULT_POPUP' }
  | { type: 'RECHARGE_ENERGY'; payload: { amount: number } }
  | { type: 'TICK_ENERGY'; payload: { now: number } }
  | { type: 'RESET' };

function getScreenAfterTitle(state: GameState): GameState['screen'] {
  if (!state.hasSeenOpening) return 'opening';
  if (!state.nickname) return 'nickname';
  if (!state.tutorialCompleted) return 'tutorial';
  return 'main';
}

function resolveElementFromRecord(state: GameState, resultName: string, fallback?: ElementInfo): ElementInfo {
  return state.elements[resultName] ?? fallback ?? {
    name: resultName,
    icon: '✨',
    tier: 0,
    category: 'Other',
    origin: 'generated'
  };
}

function applyCombineOutcome(
  state: GameState,
  payload: {
    a: string;
    b: string;
    resultName: string;
    tier: number;
    category: ElementCategory;
    description?: string | null;
    depth?: number;
    isFirstEver: boolean;
    firstDiscoverer: string;
    imageUrl?: string | null;
    discoveredCount?: number;
  }
): GameState {
  const comboKey = toComboKey(payload.a, payload.b);
  const existingRecord = state.comboCache[comboKey];
  const discoveredAt = existingRecord?.discoveredAt ?? new Date().toISOString();
  const totalDiscoveries = (existingRecord?.totalDiscoveries ?? 0) + 1;

  const comboCache = {
    ...state.comboCache,
    [comboKey]: {
      result: payload.resultName,
      tier: payload.tier,
      category: payload.category,
      firstDiscoverer: payload.firstDiscoverer,
      discoveredAt,
      totalDiscoveries
    }
  };

  const existingElement = state.elements[payload.resultName];
  const resultElement: ElementInfo = existingElement
    ? (payload.imageUrl && !existingElement.imageUrl
      ? { ...existingElement, imageUrl: payload.imageUrl, discoveredCount: payload.discoveredCount ?? existingElement.discoveredCount }
      : { ...existingElement, discoveredCount: payload.discoveredCount ?? existingElement.discoveredCount })
    : {
      name: payload.resultName,
      icon: resolveElementIcon(payload.resultName, payload.category),
      imageUrl: payload.imageUrl ?? undefined,
      description: payload.description ?? undefined,
      tier: payload.tier,
      category: payload.category,
      origin: 'generated',
      depth: payload.depth,
      discoveredCount: payload.discoveredCount
    };

  const elements = state.elements[resultElement.name]
    ? state.elements
    : { ...state.elements, [resultElement.name]: resultElement };

  const alreadyOwned = state.inventory.includes(resultElement.name);
  const nextInventory = alreadyOwned ? state.inventory : [resultElement.name, ...state.inventory];

  const popupKind = alreadyOwned
    ? 'already-owned'
    : payload.isFirstEver
      ? 'first-discovery'
      : 'discovered';

  const isTutorialCompleted = state.tutorialCompleted || comboKey === TUTORIAL_COMBO_KEY;
  const nextScreen = state.screen === 'tutorial' && isTutorialCompleted ? 'main' : state.screen;

  const nextFirstDiscoveries = payload.isFirstEver && !state.firstDiscoveries.includes(payload.resultName)
    ? [payload.resultName, ...state.firstDiscoveries]
    : state.firstDiscoveries;

  return {
    ...state,
    screen: nextScreen,
    tutorialCompleted: isTutorialCompleted,
    slotA: null,
    slotB: null,
    pendingCombine: null,
    inventory: nextInventory,
    elements,
    comboCache,
    firstDiscoveries: nextFirstDiscoveries,
    resultPopup: {
      kind: popupKind,
      result: resultElement,
      firstDiscoverer: payload.firstDiscoverer,
      isNew: !alreadyOwned,
      isFirstEver: payload.isFirstEver
    }
  };
}

function combine(state: GameState, a: string, b: string): GameState {
  if (!state.inventory.includes(a) || !state.inventory.includes(b)) {
    return state;
  }

  const comboKey = toComboKey(a, b);
  const cached = state.comboCache[comboKey];

  let resultElement: ElementInfo;
  let firstDiscoverer = state.nickname || 'Anonymous Creator';
  let isFirstEver = false;

  if (cached) {
    resultElement = resolveElementFromRecord(state, cached.result, {
      name: cached.result,
      icon: resolveElementIcon(cached.result, cached.category),
      tier: cached.tier,
      category: cached.category,
      origin: 'generated'
    });
    firstDiscoverer = cached.firstDiscoverer;
  } else {
    isFirstEver = true;
    const knownResult = KNOWN_RECIPES_BY_KEY[comboKey];

    if (knownResult) {
      resultElement = knownResult;
    } else {
      const left = state.elements[a];
      const right = state.elements[b];
      if (!left || !right) return state;
      resultElement = createGeneratedElement(left, right, new Set(Object.keys(state.elements)));
    }
  }

  return applyCombineOutcome(state, {
    a,
    b,
    resultName: resultElement.name,
    tier: resultElement.tier,
    category: resultElement.category,
    firstDiscoverer,
    isFirstEver
  });
}

export const handleSetRunning: ActionHandler<GameState, Extract<GameAction, { type: 'SET_RUNNING' }>> = (
  state,
  action
) => {
  if (state.isRunning === action.payload.running) return null;
  return { ...state, isRunning: action.payload.running };
};

export const handleStartFromTitle: ActionHandler<GameState, Extract<GameAction, { type: 'START_FROM_TITLE' }>> = (
  state
) => {
  if (state.screen !== 'title') return null;
  return { ...state, screen: getScreenAfterTitle(state), openingPage: 0 };
};

export const handleAdvanceOpening: ActionHandler<GameState, Extract<GameAction, { type: 'ADVANCE_OPENING' }>> = (
  state
) => {
  if (state.screen !== 'opening') return null;
  if (state.openingPage < OPENING_PAGE_LAST_INDEX) {
    return { ...state, openingPage: state.openingPage + 1 };
  }
  return {
    ...state,
    hasSeenOpening: true,
    screen: 'nickname',
    openingPage: OPENING_PAGE_LAST_INDEX
  };
};

export const handleSetNicknameDraft: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'SET_NICKNAME_DRAFT' }>
> = (state, action) => {
  if (state.nicknameDraft === action.payload.value) return null;
  return { ...state, nicknameDraft: action.payload.value.slice(0, 12) };
};

export const handleConfirmNickname: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'CONFIRM_NICKNAME' }>
> = (state, action) => {
  const trimmed = action.payload.nickname.trim();
  if (!NICKNAME_PATTERN.test(trimmed)) return null;
  return {
    ...state,
    nickname: trimmed,
    nicknameDraft: trimmed,
    screen: 'tutorial'
  };
};

export const handleSetAuthToken: ActionHandler<GameState, Extract<GameAction, { type: 'SET_AUTH_TOKEN' }>> = (
  state,
  action
) => {
  if (state.authToken === action.payload.token) return null;
  return { ...state, authToken: action.payload.token };
};

export const handleRestoreSession: ActionHandler<GameState, Extract<GameAction, { type: 'RESTORE_SESSION' }>> = (
  state,
  action
) => {
  const inventory = Array.from(new Set(action.payload.inventory));
  const serverElements = action.payload.serverElements;
  const elements = { ...state.elements };

  for (const name of inventory) {
    if (elements[name]) continue;
    const serverEl = serverElements?.[name];
    if (serverEl) {
      const cat = serverEl.category as ElementCategory;
      elements[name] = {
        name,
        icon: resolveElementIcon(name, cat),
        imageUrl: serverEl.image_url ?? undefined,
        description: serverEl.description ?? undefined,
        tier: serverEl.tier,
        category: cat,
        origin: 'generated',
        depth: serverEl.depth,
        discoveredCount: serverEl.discovered_count
      };
    } else {
      elements[name] = {
        name,
        icon: CATEGORY_ICONS.Other,
        tier: 0,
        category: 'Other',
        origin: 'generated'
      };
    }
  }

  const granted = grantAllEarnedMilestones(inventory, elements);

  return {
    ...state,
    nickname: action.payload.nickname,
    nicknameDraft: action.payload.nickname,
    authToken: action.payload.token,
    inventory: granted.inventory,
    elements: granted.elements,
    firstDiscoveries: action.payload.firstDiscoveries ?? state.firstDiscoveries,
    hasSeenOpening: action.payload.hasSeenOpening ?? true,
    tutorialCompleted: action.payload.tutorialCompleted ?? true
  };
};

export const handleSetActiveTab: ActionHandler<GameState, Extract<GameAction, { type: 'SET_ACTIVE_TAB' }>> = (
  state,
  action
) => {
  if (state.activeTab === action.payload.tab) return null;
  return { ...state, activeTab: action.payload.tab };
};

export const handleSetSearchQuery: ActionHandler<GameState, Extract<GameAction, { type: 'SET_SEARCH_QUERY' }>> = (
  state,
  action
) => {
  if (state.searchQuery === action.payload.query) return null;
  return { ...state, searchQuery: action.payload.query };
};

export const handleSetSortMode: ActionHandler<GameState, Extract<GameAction, { type: 'SET_SORT_MODE' }>> = (
  state,
  action
) => {
  if (state.sortMode === action.payload.mode) return null;
  return { ...state, sortMode: action.payload.mode };
};

export const handleSetCategoryFilter: ActionHandler<GameState, Extract<GameAction, { type: 'SET_CATEGORY_FILTER' }>> = (
  state,
  action
) => {
  if (state.categoryFilter === action.payload.filter) return null;
  return { ...state, categoryFilter: action.payload.filter };
};

export const handleSelectElement: ActionHandler<GameState, Extract<GameAction, { type: 'SELECT_ELEMENT' }>> = (
  state,
  action
) => {
  if (!state.inventory.includes(action.payload.name)) return null;
  if (state.pendingCombine) return null;

  let slotA = state.slotA;
  let slotB = state.slotB;

  if (!slotA) {
    slotA = action.payload.name;
  } else if (!slotB) {
    slotB = action.payload.name;
  } else {
    return null;
  }

  const willCombine = Boolean(slotA && slotB);

  if (willCombine && state.energy < 1) {
    return { ...state, slotA, slotB };
  }

  const pendingCombine = willCombine
    ? { a: slotA!, b: slotB!, remaining: COMBINE_DELAY_SECONDS, status: 'animating' as const }
    : null;

  return {
    ...state,
    slotA,
    slotB,
    pendingCombine,
    energy: pendingCombine ? state.energy - 1 : state.energy
  };
};

export const handleRemoveSlot: ActionHandler<GameState, Extract<GameAction, { type: 'REMOVE_SLOT' }>> = (
  state,
  action
) => {
  if (action.payload.slot === 'A' && !state.slotA) return null;
  if (action.payload.slot === 'B' && !state.slotB) return null;

  return {
    ...state,
    slotA: action.payload.slot === 'A' ? null : state.slotA,
    slotB: action.payload.slot === 'B' ? null : state.slotB,
    pendingCombine: null
  };
};

export const handleClearPendingCombine: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'CLEAR_PENDING_COMBINE' }>
> = (state) => {
  if (!state.pendingCombine && !state.slotA && !state.slotB) return null;
  return {
    ...state,
    slotA: null,
    slotB: null,
    pendingCombine: null
  };
};

export const handleResolvePendingCombine: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'RESOLVE_PENDING_COMBINE' }>
> = (state, action) => {
  return combine(state, action.payload.a, action.payload.b);
};

export const handleApplyServerCombineResult: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'APPLY_SERVER_COMBINE_RESULT' }>
> = (state, action) => {
  return applyCombineOutcome(state, {
    a: action.payload.a,
    b: action.payload.b,
    resultName: action.payload.result,
    tier: action.payload.tier,
    category: action.payload.category,
    description: action.payload.description,
    depth: action.payload.depth,
    isFirstEver: action.payload.isFirstEver,
    firstDiscoverer: action.payload.firstDiscoverer,
    imageUrl: action.payload.imageUrl,
    discoveredCount: action.payload.discoveredCount
  });
};

export const handleCloseResultPopup: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'CLOSE_RESULT_POPUP' }>
> = (state) => {
  if (!state.resultPopup) return null;

  if (state.resultPopup.kind !== 'milestone') {
    const milestone = getUnclaimedMilestone(state.inventory);
    if (milestone) {
      const el = getMilestoneElement(milestone);
      return {
        ...state,
        inventory: [...state.inventory, el.name],
        elements: { ...state.elements, [el.name]: el },
        resultPopup: {
          kind: 'milestone' as const,
          result: el,
          firstDiscoverer: state.nickname,
          isNew: true,
          isFirstEver: false,
          milestoneDescription: milestone.description
        }
      };
    }
  }

  return { ...state, resultPopup: null };
};

export const handleRechargeEnergy: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'RECHARGE_ENERGY' }>
> = (state, action) => {
  const next = Math.min(state.energy + action.payload.amount, MAX_ENERGY);
  if (next === state.energy) return null;
  return { ...state, energy: next, lastEnergyRechargeAt: Date.now() };
};

export const handleTickEnergy: ActionHandler<
  GameState,
  Extract<GameAction, { type: 'TICK_ENERGY' }>
> = (state, action) => {
  if (state.energy >= MAX_ENERGY) return null;
  const elapsed = action.payload.now - state.lastEnergyRechargeAt;
  const gained = Math.floor(elapsed / ENERGY_RECHARGE_INTERVAL_MS);
  if (gained <= 0) return null;
  const next = Math.min(state.energy + gained, MAX_ENERGY);
  return {
    ...state,
    energy: next,
    lastEnergyRechargeAt: state.lastEnergyRechargeAt + gained * ENERGY_RECHARGE_INTERVAL_MS
  };
};

export const handleReset: ActionHandler<GameState, Extract<GameAction, { type: 'RESET' }>> = () => {
  return createInitialState();
};

export const actionHandlers = {
  SET_RUNNING: handleSetRunning,
  START_FROM_TITLE: handleStartFromTitle,
  ADVANCE_OPENING: handleAdvanceOpening,
  SET_NICKNAME_DRAFT: handleSetNicknameDraft,
  CONFIRM_NICKNAME: handleConfirmNickname,
  SET_AUTH_TOKEN: handleSetAuthToken,
  RESTORE_SESSION: handleRestoreSession,
  SET_ACTIVE_TAB: handleSetActiveTab,
  SET_SEARCH_QUERY: handleSetSearchQuery,
  SET_SORT_MODE: handleSetSortMode,
  SET_CATEGORY_FILTER: handleSetCategoryFilter,
  SELECT_ELEMENT: handleSelectElement,
  REMOVE_SLOT: handleRemoveSlot,
  CLEAR_PENDING_COMBINE: handleClearPendingCombine,
  RESOLVE_PENDING_COMBINE: handleResolvePendingCombine,
  APPLY_SERVER_COMBINE_RESULT: handleApplyServerCombineResult,
  CLOSE_RESULT_POPUP: handleCloseResultPopup,
  RECHARGE_ENERGY: handleRechargeEnergy,
  TICK_ENERGY: handleTickEnergy,
  RESET: handleReset
} as const;
